import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    localStorage.removeItem("kohc_token");
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const finalize = useCallback((data) => {
    setUser(data.user);
    return data.user;
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    return finalize(data);
  }, [finalize]);

  const signup = useCallback(async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    return finalize(data);
  }, [finalize]);

  const logout = useCallback(() => {
    api.post("/auth/logout").catch(() => {});
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  }, []);

  const updateUser = useCallback((patch) => setUser((u) => (u ? { ...u, ...patch } : u)), []);

  const actions = useMemo(() => ({ login, signup, logout, refreshUser, updateUser }), [login, signup, logout, refreshUser, updateUser]);
  const value = useMemo(() => ({ user, loading, ...actions }), [user, loading, actions]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
